import React, { type ReactNode, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { ClassDetailEditForm, RequiredIcon } from './ClassDetailEditForm';
import { ClassDetailLoadingForm } from './ClassDetailLoadingForm';
import { ClassDetailRoster } from './ClassDetailRoster';
import DraftChip from './DraftChip';
import AttendanceView from '../../../../../components/attendance/AttendanceView';
import { LmsSyncDialog } from '../../../../../components/classroom/LmsSync/LmsSyncDialog';
import DashboardView from '../../../../../components/dashboard/DashboardView';
import AttendanceLayout from '../../../../../components/layout/AttendanceLayout';
import { Recordings } from '../../../../../components/recordings/Recordings';
import { AttendanceProvider, View } from '../../../../../context/AttendanceContext';
import { DashboardProvider } from '../../../../../context/DashboardContext';
import { RosterProvider } from '../../../../../context/RosterContext';
import { ClassStatus } from '../../../../../context/types';
import { useOrgSettings } from '../../../../../hooks/useOrgSettings';
import { useQuery } from '../../../../../hooks/useQuery';
import { useRecordings } from '../../../../../hooks/useRecordings';
import { getUrlParams } from '../../../../../utils/url';
import { ClassContainer } from '../../../../ClassModal/ClassContainer';
import ErrorForm from '../../../../ClassModal/ErrorForm';
import AdminAuthContainer from '../../../AdminAuthContainer';
import StyledFormTitle from '../../../styles/form-title';
import ClassDetailEditFormActions from '../ClassDetailEditFormActions';
import CopyUrlButton from '../CopyUrlButton';
import { DownloadChatModal } from '../DownloadChat/DownloadChatModal';
import { DownloadTranscriptModal } from '../DownloadChat/DownloadTranscriptModal';

interface TabPanelProps {
    children?: ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`class-detail-tabpanel-${index}`}
            aria-labelledby={`class-detail-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `class-detail-tab-${index}`,
        'aria-controls': `class-detail-tabpanel-${index}`
    };
}

const PageTitle = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

export const StyledActionButton = styled(Button)`
    margin-right: 0.75rem;
`;

const StyledUrlButton = styled(CopyUrlButton)`
    margin-right: 0.75rem;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
`;

const BreadcrumbTitle = styled.div`
    display: flex;
    align-items: center;
`;

const ClassTitle = styled.span`
    color: ${(props) => props.theme.colors.black};
`;

enum TabTypes {
    ClassDetails = 0,
    Roster = 1,
    Dashboard = 2,
    Attenance = 3,
    Recording = 4
}

export const ClassDetail = () => {
    const { t } = useTranslation(['admin_settings', 'create_edit_class', 'participant_sidebar']);
    const {
        classRecord,
        handleSubmit,
        updateClass,
        getClassToEditStatus,
        errorMessage,
        userUuid,
        getClass,
        lmsSyncSettings,
        recordings,
        setRecordings
    } = useContainer(ClassContainer);

    const query = useQuery();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const { canManageClasses } = useContainer(AdminAuthContainer);

    const [tabValue, setTabValue] = useState(TabTypes.ClassDetails);
    const [downloadChatModalIsOpen, setDownloadChatModalIsOpen] = useState(false);
    const [downloadTranscriptModalIsOpen, setDownloadTranscriptModalIsOpen] = useState(false);
    const [showLmsSyncModal, setShowLmsSyncModal] = useState(false);
    const lmsType = classRecord?.lms_type;

    const canLmsSync = !!lmsSyncSettings?.sync_enabled && !!classRecord?.sid && !!lmsType;
    const isDraft = classRecord?.status === ClassStatus.Draft;

    const onSubmit = useCallback(async () => {
        await updateClass({
            onSuccess: () => enqueueSnackbar(t('admin_settings:classes.update-success'), { variant: 'success' }),
            onError: () => enqueueSnackbar(t('admin_settings:classes.update-error'), { variant: 'error' })
        });
    }, [enqueueSnackbar, t, updateClass]);

    const renderedForm = useMemo(() => {
        if (getClassToEditStatus === 'SUCCESS') {
            return <ClassDetailEditForm onSubmit={handleSubmit(onSubmit)} />;
        }
        if (getClassToEditStatus === 'ERROR') {
            return <ErrorForm errorMessage={errorMessage} />;
        }

        return <ClassDetailLoadingForm />;
    }, [getClassToEditStatus, handleSubmit, onSubmit, errorMessage]);

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleLmsSyncOpen = useCallback(() => {
        if (!lmsType) {
            console.error('No LMS type found');
            return;
        }
        setShowLmsSyncModal(true);
    }, [lmsType]);

    const handleLmsSyncClose = async () => {
        setShowLmsSyncModal(false);
    };

    const handleLmsSyncComplete = async () => {
        await getClass();
    };

    const renderHeaderActions = () => {
        if (getClassToEditStatus === 'LOADING') {
            return null;
        }

        const components: React.ReactNode[] = [];

        if (!isDraft && canLmsSync) {
            components.push(
                <StyledActionButton onClick={handleLmsSyncOpen} variant="outlined">
                    {t('lti:sync-lms-data')}
                </StyledActionButton>
            );
        }

        if (!isDraft && classRecord) {
            components.push(<StyledUrlButton classUuid={classRecord?.class_uuid || ''} />);
        }

        if (!isDraft) {
            components.push(
                <StyledActionButton onClick={() => setDownloadChatModalIsOpen(true)} variant="outlined">
                    {t('admin_settings:classes.download-chat')}
                </StyledActionButton>
            );
        }
        if (!isDraft) {
            components.push(
                <StyledActionButton onClick={() => setDownloadTranscriptModalIsOpen(true)} variant="outlined">
                    {t('admin_settings:classes.download-transcript')}
                </StyledActionButton>
            );
        }

        components.push(<ClassDetailEditFormActions />);

        return components;
    };

    const renderClassTitle = () => {
        if (getClassToEditStatus === 'LOADING') {
            return <Skeleton animation="wave" width={150} height={24} />;
        }

        if (!classRecord) {
            return null;
        }

        const title = classRecord?.class_name || query.get('className');

        if (isDraft) {
            return (
                <BreadcrumbTitle>
                    <ClassTitle>{title}</ClassTitle>
                    <DraftChip />
                </BreadcrumbTitle>
            );
        }

        return title;
    };

    const urlParams = useMemo(() => {
        const search = new URLSearchParams(window.location.search);
        return {
            sessionId: search.get('session_id'),
            isDemo: search.get('isDemo')
        };
    }, []);

    const meetingProvider = classRecord?.meeting_provider;
    const isZoomProvider = meetingProvider && meetingProvider.trim().toLowerCase() === 'zoom';

    const { legacyRecordings } = getUrlParams();
    const { data: classSettings } = useOrgSettings();

    const showLegacyRecordings = Boolean(
        isZoomProvider && (legacyRecordings || classSettings?.featureSettings.recordingLegacyZoomList)
    );

    const { refetchRecordings, data, isLoadingRecordings, isFetchingRecordings } = useRecordings({
        userUuid,
        classUuid: classRecord?.class_uuid,
        meetingId: classRecord?.meeting_id,
        showLegacyRecordings,
        doFetch: tabValue === TabTypes.Recording
    });

    useEffect(() => {
        setRecordings(data ?? []);
    }, [setRecordings, data]);

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <PageTitle>{t('admin_settings:classes.title')}</PageTitle>
                </Grid>
                <HeaderActions>{renderHeaderActions()}</HeaderActions>
            </Grid>

            <Breadcrumbs aria-label="breadcrumb">
                <Link to="/adminSettings/classes">{t('admin_settings:classes.all-classes')}</Link>
                <span>{renderClassTitle()}</span>
            </Breadcrumbs>

            <Paper elevation={0} sx={{ mt: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label={t('admin_settings:classes.class-detail-tabs')}
                >
                    <Tab label={t('admin_settings:classes.details')} {...a11yProps(TabTypes.ClassDetails)} />
                    <Tab label={t('admin_settings:classes.roster')} {...a11yProps(TabTypes.Roster)} />
                    <Tab label={t('participant_sidebard:Dashboard')} {...a11yProps(TabTypes.Dashboard)} />
                    <Tab label={t('participant_sidebard:Attendance')} {...a11yProps(TabTypes.Attenance)} />
                    <Tab
                        label={
                            <Box position="relative">
                                {t('class:recordings')}
                                {tabValue === 4 && isFetchingRecordings && (
                                    <CircularProgress
                                        sx={{ position: 'absolute', top: 'calc(50% - 8px)', right: '-20px' }}
                                        size={16}
                                    />
                                )}
                            </Box>
                        }
                        {...a11yProps(TabTypes.Recording)}
                    />
                </Tabs>
            </Paper>

            <TabPanel value={tabValue} index={TabTypes.ClassDetails} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        <Typography>
                            <RequiredIcon /> {t('admin_settings:classes.required-fields')}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {renderedForm}
                    </CardContent>
                </Card>
            </TabPanel>
            <TabPanel value={tabValue} index={TabTypes.Roster} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        {classRecord && (
                            <RosterProvider
                                classId={classRecord.sid as unknown as string}
                                classUuid={classRecord.class_uuid!}
                                userUuid={window.localStorage?.user_uuid as string}
                                isDemo={'false'}
                            >
                                <ClassDetailRoster />
                            </RosterProvider>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>
            <TabPanel value={tabValue} index={TabTypes.Dashboard} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        {classRecord && (
                            <DashboardProvider
                                sessionUuid={urlParams.sessionId}
                                classUuid={classRecord.class_uuid!}
                                isDemo={urlParams.isDemo}
                            >
                                <DashboardView />
                            </DashboardProvider>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>
            <TabPanel value={tabValue} index={TabTypes.Attenance} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        {classRecord && (
                            <AttendanceProvider
                                classId={classRecord.class_uuid!}
                                classSessionId="session-summary"
                                view={View.SessionSummary}
                            >
                                <AttendanceLayout>
                                    <AttendanceView />
                                </AttendanceLayout>
                            </AttendanceProvider>
                        )}
                    </CardContent>
                </Card>
            </TabPanel>
            <TabPanel value={tabValue} index={TabTypes.Recording} dir={theme.direction}>
                <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                    <CardContent>
                        {classRecord && (
                            <Recordings
                                showLegacyRecordings={showLegacyRecordings}
                                recordings={recordings}
                                setRecordings={setRecordings}
                                refetchRecordings={refetchRecordings}
                                isLoadingRecordings={isLoadingRecordings}
                                isFetchingRecordings={isFetchingRecordings}
                                hasAdminPrivileges={canManageClasses}
                            />
                        )}
                    </CardContent>
                </Card>
            </TabPanel>

            <DownloadChatModal
                open={downloadChatModalIsOpen}
                onClose={() => setDownloadChatModalIsOpen(false)}
                classId={classRecord?.sid}
                className={classRecord?.class_name}
                userUuid={userUuid}
            />
            <DownloadTranscriptModal
                open={downloadTranscriptModalIsOpen}
                onClose={() => setDownloadTranscriptModalIsOpen(false)}
                classId={classRecord?.sid}
                className={classRecord?.class_name}
                userUuid={userUuid}
                classUuid={classRecord?.class_uuid}
            />

            {classRecord?.class_uuid && (
                <LmsSyncDialog
                    open={showLmsSyncModal}
                    onClose={handleLmsSyncClose}
                    onComplete={handleLmsSyncComplete}
                    classId={String(classRecord?.sid)}
                    classUuid={classRecord?.class_uuid}
                    lmsType={lmsType!}
                    userUuid={userUuid!}
                    lmsSyncSettings={lmsSyncSettings}
                />
            )}
        </>
    );
};
